import { FocusEventHandler, MouseEventHandler, useRef, useState } from 'react';
import styles from './ColorPicker.module.scss';

import { useOnClickOutside } from 'app/hooks';
import { Icon } from '../Icon';

interface SelectProps {
  id?: string;
  selected?: string;
  options: string[];
  placeholder?: string;
  disabled?: boolean;
  onChange?: (selected: string) => void;
  onClose?: () => void;
  onBlur?: FocusEventHandler<HTMLDivElement> | undefined;
}

interface OptionProps {
  id?: string;
  selected: boolean;
  option: string;
  onClick: (value: string) => void;
}

const Option = ({ option, selected, onClick, id }: OptionProps) => {
  const optionRef = useRef<HTMLLIElement>(null);

  const handleClick =
    (clickedValue: string): MouseEventHandler<HTMLLIElement> =>
      () => {
        onClick(clickedValue);
      };

  return (
    <li
      id={`${id}-${option}`}
      className={styles.option}
      data-color={option}
      data-selected={selected}
      style={{ backgroundColor: option }}
      ref={optionRef}
      value={option}
      onClick={handleClick(option)}
      tabIndex={0}
    />
  );
};

export const ColorPicker = ({ onClose, selected, placeholder, onChange, onBlur, options, disabled, id }: SelectProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useOnClickOutside(rootRef, () => {
    setIsOpen(false);
  });

  const placeholderRef = useRef<HTMLDivElement>(null);

  const handleOptionClick = (value: string) => {
    onChange?.(value);
    onClose?.();
  };

  const handleClick = () => {
    setIsOpen((prev) => !prev);
  };

  return (
    <div
      id={id}
      tabIndex={0}
      ref={rootRef}
      className={styles.selectWrapper}
      data-is-active={isOpen}
      data-aria-disabled={disabled}
      onClick={handleClick}
      onBlur={onBlur}
    >
      <div className={styles.placeholder} data-selected={!!selected} ref={placeholderRef}>
        <div className={styles.color_block} style={{ backgroundColor: selected }}></div>
        {selected ? '' : placeholder}
        <Icon name="arrow-down" />
      </div>

      {isOpen && (
        <ul className={styles.select}>
          {options.map((option) => (
            <Option key={option} selected={selected === option} id={id} option={option} onClick={handleOptionClick} />
          ))}
        </ul>
      )}
    </div>
  );
};
